import React, { useState, useCallback, useEffect } from "react";
import useApi from "@/hooks/useApi";

export interface DnsRecord {
  type: string;
  name: string;
  value: string;
  description?: string;
  expectedContains?: string;
}

interface DnsVerifyResult {
  type: string;
  name: string;
  status: "verified" | "missing" | "error";
  value: string | null;
}

interface DnsPushResult {
  type: string;
  name: string;
  success: boolean;
  error?: string;
}

interface CredField {
  key: string;
  label: string;
  hint: string;
  type: "text" | "password";
}

interface Provider {
  id: string;
  name: string;
  dashboardUrl: string;
  supportsAutoPush: boolean;
  supportsOAuth?: boolean;
  credFields: CredField[];
}

interface CloudflareOAuthZone {
  id: string;
  name: string;
  status: string;
}

interface CloudflareOAuthStatus {
  connected: boolean;
  connectedAt?: string;
  connectedBy?: string;
  tokenExpiresAt?: string;
  zones?: CloudflareOAuthZone[];
}

interface Props {
  domain: string;
  records: DnsRecord[];
  onVerified?: () => void;
}

const MANUAL_PROVIDERS = [
  { id: "namecheap",   name: "Namecheap",           url: "https://ap.www.namecheap.com/domains/dns-settings" },
  { id: "route53",     name: "Route 53",             url: "https://console.aws.amazon.com/route53/v2/hostedzones" },
  { id: "google",      name: "Google Domains",       url: "https://domains.google.com" },
  { id: "easydns",     name: "EasyDNS",              url: "https://cp.easydns.com" },
  { id: "squarespace", name: "Squarespace",          url: "https://account.squarespace.com/domains" },
  { id: "porkbun",     name: "Porkbun",              url: "https://porkbun.com/account/domainsSpeedy" },
];

export default function DnsSetupWizard({ domain, records, onVerified }: Props) {
  const api = useApi();
  const [copied, setCopied] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [verifyResults, setVerifyResults] = useState<DnsVerifyResult[] | null>(null);
  const [allVerified, setAllVerified] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Auto-push state
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [pushing, setPushing] = useState(false);
  const [pushResults, setPushResults] = useState<DnsPushResult[] | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [showAutoPush, setShowAutoPush] = useState(false);

  // Cloudflare OAuth state
  const [cfOAuth, setCfOAuth] = useState<CloudflareOAuthStatus | null>(null);
  const [cfOAuthLoading, setCfOAuthLoading] = useState(false);
  const [cfConnecting, setCfConnecting] = useState(false);
  const [cfDisconnecting, setCfDisconnecting] = useState(false);

  useEffect(() => {
    api.admin.get("DnsAutoSetup/providers")
      .then(r => r.json())
      .then((data: Provider[]) => setProviders(data))
      .catch(() => {});
  }, [api]);

  // Load Cloudflare OAuth status on mount
  useEffect(() => {
    setCfOAuthLoading(true);
    api.admin.get("Cloudflare/OAuth/Status")
      .then(r => r.json())
      .then((data: CloudflareOAuthStatus) => setCfOAuth(data))
      .catch(() => setCfOAuth({ connected: false }))
      .finally(() => setCfOAuthLoading(false));
  }, [api]);

  const connectCloudflareOAuth = useCallback(async () => {
    setCfConnecting(true);
    try {
      const r = await api.admin.get("Cloudflare/OAuth/AuthUrl");
      const d = await r.json();
      if (r.ok && d.authUrl) {
        window.location.href = d.authUrl;
      }
    } catch {
      // ignore — redirect
    } finally {
      setCfConnecting(false);
    }
  }, [api]);

  const disconnectCloudflareOAuth = useCallback(async () => {
    setCfDisconnecting(true);
    try {
      await api.admin.delete("Cloudflare/OAuth/Disconnect");
      setCfOAuth({ connected: false });
    } catch {
      // ignore
    } finally {
      setCfDisconnecting(false);
    }
  }, [api]);

  const copy = useCallback((val: string, key: string) => {
    navigator.clipboard.writeText(val).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(c => (c === key ? null : c)), 2000);
  }, []);

  const verify = useCallback(async () => {
    setChecking(true);
    setVerifyError(null);
    try {
      const r = await api.admin.post("DnsAutoSetup/verify", {
        domain,
        records: records.map(r => ({ type: r.type, name: r.name, expectedContains: r.expectedContains ?? null })),
      });
      const d = await r.json();
      if (r.ok) {
        setVerifyResults(d.results ?? []);
        setAllVerified(d.allVerified === true);
        if (d.allVerified && onVerified) onVerified();
      } else {
        setVerifyError(d.error ?? "Verification failed.");
      }
    } catch {
      setVerifyError("Network error. Check your connection and try again.");
    } finally {
      setChecking(false);
    }
  }, [api, domain, records, onVerified]);

  const pushRecords = useCallback(async () => {
    if (!selectedProvider) return;
    setPushing(true);
    setPushError(null);
    setPushResults(null);
    try {
      const r = await api.admin.post("DnsAutoSetup/push-records", {
        provider: selectedProvider.id,
        domain,
        credentials: creds,
        records: records.map(r => ({ type: r.type, name: r.name, value: r.value })),
      });
      const d = await r.json();
      if (r.ok) {
        setPushResults(d.results ?? []);
        if (d.allPushed) {
          // Auto-verify after successful push
          setTimeout(() => verify(), 2000);
        }
      } else {
        setPushError(d.error ?? "Push failed.");
      }
    } catch {
      setPushError("Network error during DNS push.");
    } finally {
      setPushing(false);
    }
  }, [api, domain, records, selectedProvider, creds, verify]);

  // Push DNS records to Cloudflare using the stored OAuth token (zone ID from creds["zoneId"])
  const pushCloudflareOAuth = useCallback(async () => {
    setPushing(true);
    setPushError(null);
    setPushResults(null);
    try {
      const r = await api.admin.post("DnsAutoSetup/push-records", {
        provider: "cloudflare",
        domain,
        credentials: creds,
        records: records.map(rec => ({ type: rec.type, name: rec.name, value: rec.value })),
      });
      const d = await r.json();
      if (r.ok) {
        setPushResults(d.results ?? []);
        if (d.allPushed) {
          setTimeout(() => verify(), 2000);
        }
      } else {
        setPushError(d.error ?? "Push failed.");
      }
    } catch {
      setPushError("Network error during DNS push.");
    } finally {
      setPushing(false);
    }
  }, [api, domain, records, creds, verify]);

  const statusFor = (idx: number) => verifyResults?.[idx]?.status ?? null;

  if (allVerified) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
        <div className="flex justify-center mb-3">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <p className="text-green-800 font-semibold text-sm">All DNS records verified!</p>
        <p className="text-green-600 text-xs mt-1">Your domain <span className="font-mono font-medium">{domain}</span> is fully configured.</p>
      </div>
    );
  }

  const autoPushProviders = providers.filter(p => p.supportsAutoPush);

  return (
    <div className="space-y-4">
      {/* DNS Records table */}
      <div className="space-y-2">
        {records.map((rec, i) => {
          const st = statusFor(i);
          const rowBg = st === "verified" ? "bg-green-50 border-green-200"
            : st === "missing" || st === "error" ? "bg-red-50 border-red-200"
            : "bg-gray-50 border-gray-200";
          const copyKey = `${rec.type}-${rec.name}-${i}`;
          return (
            <div key={i} className={`border rounded-lg p-3 text-xs ${rowBg}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="font-mono font-bold text-gray-700 bg-white border border-gray-200 rounded px-1.5 py-0.5">{rec.type}</span>
                <span className="font-mono text-gray-600">{rec.name || "@"}</span>
                {st === "verified" && (
                  <span className="ml-auto text-green-600 font-medium flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    Verified
                  </span>
                )}
                {(st === "missing" || st === "error") && (
                  <span className="ml-auto text-red-600 font-medium flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                    {st === "error" ? "Lookup error" : "Not found"}
                  </span>
                )}
                {pushResults && (
                  <span className={`ml-auto font-medium flex items-center gap-1 ${pushResults[i]?.success ? "text-blue-600" : "text-red-500"}`}>
                    {pushResults[i]?.success ? "✓ Pushed" : `✗ ${pushResults[i]?.error ?? "Failed"}`}
                  </span>
                )}
                {!st && !pushResults && <span className="ml-auto text-gray-400 font-medium">Pending</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <p className="font-mono text-gray-700 bg-white rounded px-2 py-1 border border-gray-200 break-all flex-1 text-[11px] leading-relaxed">
                  {rec.value}
                </p>
                <button onClick={() => copy(rec.value, copyKey)} title="Copy value"
                  className="shrink-0 p-1.5 text-gray-400 hover:text-gray-700 border border-gray-200 bg-white rounded transition-colors">
                  {copied === copyKey ? (
                    <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </div>
              {rec.description && <p className="text-gray-500 mt-1">{rec.description}</p>}
            </div>
          );
        })}
      </div>

      {/* Cloudflare OAuth section */}
      {!cfOAuthLoading && (
        <div className="border border-orange-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-orange-50 flex items-center gap-2">
            <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 24 24"><path d="M13.5 3C8.25 3 4 7.25 4 12.5S8.25 22 13.5 22 23 17.75 23 12.5 18.75 3 13.5 3zm0 2c4.14 0 7.5 3.36 7.5 7.5S17.64 20 13.5 20 6 16.64 6 12.5 9.36 5 13.5 5z"/></svg>
            <span className="text-sm font-medium text-orange-800">Cloudflare — Connect via OAuth</span>
            {cfOAuth?.connected && (
              <span className="ml-auto text-xs text-green-700 bg-green-100 border border-green-200 rounded px-2 py-0.5 font-medium">Connected</span>
            )}
          </div>
          <div className="p-4 bg-white space-y-3">
            {cfOAuth?.connected ? (
              <>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>Connected by <span className="font-medium">{cfOAuth.connectedBy}</span></p>
                  {(cfOAuth.zones?.length ?? 0) > 0 ? (
                    <p>{cfOAuth.zones!.length} zone{cfOAuth.zones!.length !== 1 ? "s" : ""} available: {cfOAuth.zones!.map(z => z.name).join(", ")}</p>
                  ) : (
                    <p className="text-gray-400">No zones found in this account.</p>
                  )}
                </div>
                <p className="text-xs text-gray-500">DNS records will be pushed using this OAuth token. You still need to provide the Zone ID below.</p>
                {pushError && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{pushError}</p>
                )}
                {pushResults && pushResults.every(r => r.success) && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                    All records pushed! Verifying DNS propagation…
                  </p>
                )}
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Zone ID</label>
                    <input
                      type="text"
                      value={creds["zoneId"] ?? ""}
                      onChange={e => setCreds(c => ({ ...c, zoneId: e.target.value }))}
                      placeholder="Found in the Overview tab of your domain in Cloudflare"
                      className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={pushCloudflareOAuth}
                    disabled={pushing || !creds["zoneId"]?.trim()}
                    className="w-full py-2 text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                  >
                    {pushing ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                        Pushing records…
                      </span>
                    ) : "Push to Cloudflare"}
                  </button>
                </div>
                <button
                  onClick={disconnectCloudflareOAuth}
                  disabled={cfDisconnecting}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  {cfDisconnecting ? "Disconnecting…" : "Disconnect Cloudflare"}
                </button>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500">Connect your Cloudflare account once and we'll push DNS records automatically — no API token needed.</p>
                <button
                  onClick={connectCloudflareOAuth}
                  disabled={cfConnecting}
                  className="w-full py-2 text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                >
                  {cfConnecting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                      Redirecting…
                    </span>
                  ) : "Connect Cloudflare Account"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Auto-push section (manual credentials fallback) */}
      {autoPushProviders.length > 0 && (
        <div className="border border-blue-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowAutoPush(s => !s)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 text-sm font-medium text-blue-800 hover:bg-blue-100 transition-colors"
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              Auto-configure via API token (manual)
            </span>
            <svg className={`w-4 h-4 transition-transform ${showAutoPush ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
          </button>

          {showAutoPush && (
            <div className="p-4 space-y-3 bg-white">
              <p className="text-xs text-gray-500">Enter your API credentials below and we'll add all DNS records automatically. Credentials are used once and never stored.</p>

              {/* Provider selector */}
              <div className="flex flex-wrap gap-2">
                {autoPushProviders.map(p => (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedProvider(p); setCreds({}); setPushResults(null); setPushError(null); }}
                    className={`px-3 py-1.5 text-xs rounded-md font-medium border transition-colors ${
                      selectedProvider?.id === p.id
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>

              {selectedProvider && (
                <div className="space-y-2 pt-1">
                  {selectedProvider.credFields.map(field => (
                    <div key={field.key}>
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">{field.label}</label>
                      <input
                        type={field.type}
                        value={creds[field.key] ?? ""}
                        onChange={e => setCreds(c => ({ ...c, [field.key]: e.target.value }))}
                        placeholder={field.hint}
                        className="w-full text-xs border border-gray-300 rounded px-2.5 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <p className="text-[11px] text-gray-400 mt-0.5">{field.hint}</p>
                    </div>
                  ))}

                  {pushError && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{pushError}</p>
                  )}

                  {pushResults && pushResults.every(r => r.success) && (
                    <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                      All records pushed! Verifying DNS propagation (this can take a few minutes)…
                    </p>
                  )}

                  <button
                    onClick={pushRecords}
                    disabled={pushing || selectedProvider.credFields.some(f => !creds[f.key]?.trim())}
                    className="w-full py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {pushing ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                        Pushing records…
                      </span>
                    ) : `Push to ${selectedProvider.name}`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Manual provider links */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-xs font-medium text-gray-600 mb-2">Open DNS control panel manually:</p>
        <div className="flex flex-wrap gap-2">
          {MANUAL_PROVIDERS.map(p => (
            <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer"
              className="text-xs px-2.5 py-1 bg-white border border-gray-300 text-gray-600 rounded-md hover:border-blue-400 hover:text-blue-700 transition-colors font-medium">
              {p.name} ↗
            </a>
          ))}
        </div>
      </div>

      {verifyError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{verifyError}</p>
      )}

      {verifyResults && !allVerified && (
        <div className="text-xs bg-yellow-50 border border-yellow-200 rounded px-3 py-2 text-yellow-800">
          Some records are still missing. DNS changes can take up to 48 hours. Try verifying again in a few minutes.
        </div>
      )}

      <button onClick={verify} disabled={checking}
        className="w-full py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
        {checking ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
            Checking DNS…
          </span>
        ) : "Verify DNS Records"}
      </button>

      <p className="text-[11px] text-gray-400 text-center">DNS changes can take up to 48 hours to propagate.</p>
    </div>
  );
}
